import * as React from 'react';
import { cn } from '@/lib/utils';
import { ButtonProps } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role='navigation'
    aria-label='pagination'
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn('flex flex-row flex-wrap items-center justify-center gap-1', className)}
      {...props}
    />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn('', className)}
      {...props}
    />
  ),
);
PaginationItem.displayName = 'PaginationItem';

type PaginationButtonProps = {
  isActive?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<typeof Button>;

const PaginationLink = ({ className, isActive, size = 'sm', ...props }: PaginationButtonProps) => (
  <Button
    aria-current={isActive ? 'page' : undefined}
    variant={isActive ? 'default' : 'outline'}
    className={cn(
      'bg-transparent text-black hover:bg-white hover:text-black focus:bg-white focus:text-black active:bg-black [&[aria-current]>div]:bg-green-500',
      className,
    )}
    childClassName={`border-green-500 group:border-green-500 group-hover:border-green-500 group-hover:after:bg-green-500  group-active:after:bg-white group-active-hover:after:bg-green-500 group-focus:after:bg-green-500 border-0 ${isActive ? 'bg-green-500 border-green-500' : ''}`}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to previous page'
    size='sm'
    className={cn('h-8 w-8 gap-1 px-0', className)}
    {...props}
  >
    <img
      className='h-4 w-5'
      src='/images/arrow-left-black.svg'
      alt='Left arrow image'
      title=''
    />
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to next page'
    size='sm'
    className={cn('h-8 w-8 gap-1 px-0', className)}
    {...props}
  >
    <img
      className='h-4'
      src='/images/arrow-right-black.svg'
      alt='right arrow image'
      title=''
    />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-end justify-center', className)}
    {...props}
  >
    <DotsHorizontalIcon className='h-4 w-4' />
    <span className='sr-only'>More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
